// TanStack Query hooks for the staff feature.
// Staff CRUD via /staff, /staff/:id, /staff/:id/deactivate.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api/client";
import { serverErrorMessage } from "@/lib/api/error-message";
import type { components, paths } from "@/lib/api/generated/schema";
import { apiErrorMessage } from "@/features/proofs/api";
import type { StaffInput } from "./schemas";

// Thân POST /staff lấy thẳng từ đúng cửa đó. Trước đây suy từ
// `Parameters<typeof api.POST>[1]["body"]` — hợp của MỌI cửa POST — nên hợp
// đồng thêm cửa POST là tsc vượt trần độ sâu kiểu (TS2589, 01/10/2026).
type StaffCreateBody = NonNullable<paths["/staff"]["post"]["requestBody"]>["content"]["application/json"];

// ══════════ STAFF QUERIES ══════════

export function useStaff(department?: string) {
  return useQuery({
    queryKey: ["staff", { department: department ?? null }],
    queryFn: async () => {
      const { data, error } = await api.GET("/staff", {
        params: { query: department ? { department } : {} },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Failed to load staff"));
      return data;
    },
    staleTime: 60_000,
  });
}

// Khoá hồ sơ HRM làm chủ (CEO chốt 1a, 29/09/2026): bật thì thêm người, cho
// nghỉ việc làm ở HRM — màn ẩn các nút đó. Máy chủ vẫn chặn 409 dù màn ẩn hay
// không, nên đây chỉ là để không bày nút chắc chắn bị từ chối.
export function useStaffRecordOwnership() {
  return useQuery({
    queryKey: ["staff", "record-ownership"],
    queryFn: async () => {
      const { data, error } = await api.GET("/staff/record-ownership", {});
      if (error || !data) throw new Error(serverErrorMessage(error, "Không đọc được ai làm chủ hồ sơ nhân sự"));
      return data;
    },
    staleTime: 5 * 60_000,
  });
}

export function useStaffGrouped() {
  return useQuery({
    queryKey: ["staff", "grouped"],
    queryFn: async () => {
      const { data, error } = await api.GET("/staff/grouped", {});
      if (error || !data) throw new Error(serverErrorMessage(error, "Failed to load grouped staff"));
      return data;
    },
    staleTime: 60_000,
  });
}

export function useSaleStaffOptions() {
  return useQuery({
    queryKey: ["staff", "sale-options"],
    queryFn: async () => {
      const { data, error } = await api.GET("/staff/sale-options", {});
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được danh sách nhân viên kinh doanh"));
      return data;
    },
    staleTime: 60_000,
  });
}

export function useStaffById(id: string | null) {
  return useQuery({
    queryKey: ["staff", id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await api.GET("/staff/{id}", {
        params: { path: { id } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được hồ sơ nhân sự"));
      return data;
    },
    enabled: !!id,
  });
}

// ══════════ STAFF MUTATIONS ══════════

// Prefixes StaffService puts on a Casdoor failure (backend/internal/core/hr/
// staff_service.go). Every one of them means the edit was rolled back — the
// staff row and the login account were left as they were.
const CASDOOR_CALL_FAILED =
  /^(?:Đồng bộ Casdoor|Đổi vai trên Casdoor|Tạo tài khoản Casdoor|Gán role Casdoor) thất bại:\s*([\s\S]*)$/;
// Transport-level failures: Casdoor was never reached, or answered with a
// proxy error page ("casdoor decode": non-JSON body) instead of its API.
const CASDOOR_UNREACHABLE =
  /dial tcp|connection refused|connection reset|no such host|i\/o timeout|Client\.Timeout|deadline exceeded|\bEOF\b|casdoor decode|TLS handshake|server misbehaving/i;
// The linked account is gone: ErrUserNotFound from the client, or Casdoor's
// own "The user: org/name doesn't exist".
const CASDOOR_ACCOUNT_GONE = /user not found|\bThe user\b.*\b(?:doesn't|does not) exist/i;

/**
 * The reason a staff save failed, in words HR can act on.
 *
 * The server already says why (a Vietnamese validation message, or a Casdoor
 * refusal with its English reason appended); throwing a fixed "Lỗi khi lưu"
 * hid whether the phone, the e-mail or Casdoor itself was the problem — and
 * whether anything had been saved.
 */
export function staffSaveErrorMessage(error: unknown, fallback: string): string {
  const message = apiErrorMessage(error, "");
  const casdoor = CASDOOR_CALL_FAILED.exec(message);
  if (!casdoor) return message || fallback;
  const detail = casdoor[1] ?? "";
  if (/phone already exists/i.test(detail)) {
    return "Số điện thoại này đã thuộc một tài khoản đăng nhập khác — chưa lưu gì";
  }
  if (/email already exists/i.test(detail)) {
    return "E-mail này đã thuộc một tài khoản đăng nhập khác — chưa lưu gì";
  }
  if (CASDOOR_UNREACHABLE.test(detail)) {
    return "Không liên lạc được hệ đăng nhập Casdoor — chưa lưu gì, thử lại sau";
  }
  if (CASDOOR_ACCOUNT_GONE.test(detail)) {
    return "Không tìm thấy tài khoản đăng nhập của nhân viên này trên Casdoor — chưa lưu gì. Báo quản trị nối lại tài khoản";
  }
  return `Hệ đăng nhập Casdoor từ chối — chưa lưu gì (lời gốc: ${detail.replace(/casdoor:\s*/gi, "")})`;
}

// Thân PUT /staff/{id} neo vào đúng cửa đó — cùng lý do với `StaffCreateBody`
// ở đầu tệp: `Parameters<typeof api.PUT>[1]` là hợp của MỌI cửa PUT.
type StaffUpdateBody = NonNullable<
  paths["/staff/{id}"]["put"]["requestBody"]
>["content"]["application/json"];

export function useCreateStaff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: StaffInput) => {
      // Strip undefined fields and FE-only provisionCasdoor flag before sending
      // casdoorPassword is included only when provisionCasdoor was true (non-empty)
      const { provisionCasdoor, ...rest } = input;
      const apiInput = provisionCasdoor
        ? rest
        : (({ casdoorPassword: _pw, ...r }) => r)(rest);
      const body = Object.fromEntries(
        Object.entries(apiInput).filter(([, v]) => v !== undefined),
      ) as StaffCreateBody;
      const { data, error } = await api.POST("/staff", { body });
      if (error || !data) throw new Error(staffSaveErrorMessage(error, "Lỗi khi lưu"));
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["staff"] }),
  });
}

export function useUpdateStaff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: StaffInput }) => {
      // Strip FE-only provisioning fields — update never provisions Casdoor
      const { provisionCasdoor: _p, casdoorPassword: _pw, ...apiInput } = input;
      const body = Object.fromEntries(
        Object.entries(apiInput).filter(([, v]) => v !== undefined),
      ) as StaffUpdateBody;
      const { data, error } = await api.PUT("/staff/{id}", {
        params: { path: { id } },
        body,
      });
      if (error || !data) throw new Error(staffSaveErrorMessage(error, "Lỗi khi lưu"));
      return data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["staff"] });
      qc.invalidateQueries({ queryKey: ["staff", vars.id] });
    },
  });
}

export function useDeactivateStaff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.POST("/staff/{id}/deactivate", {
        params: { path: { id } },
      });
      // Khoá HRM làm chủ hồ sơ (409) có lý do tiếng Việt — phải tới được màn.
      if (error) throw new Error(serverErrorMessage(error, "Không cho nghỉ việc được nhân viên này"));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["staff"] }),
  });
}

// ══════════ BRANCH CATALOG + CASDOOR SCOPES ══════════

export type BranchUnit = components["schemas"]["BranchUnit"];
export type StaffBranchScope = components["schemas"]["StaffBranchScope"];
export type BranchUnitInput = components["schemas"]["BranchUnitCreateInput"];
export type BranchUnitUpdate = components["schemas"]["BranchUnitUpdateInput"];

export function useBranches() {
  return useQuery({
    queryKey: ["branches"],
    queryFn: async () => {
      const { data, error } = await api.GET("/branches", {});
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được danh sách chi nhánh"));
      return data;
    },
    staleTime: 60_000,
  });
}

/**
 * The branch was written but its Casdoor scope policy was not.
 *
 * This must never be reported as a plain failure: the row exists, so retrying
 * the same create collides with the unique constraint, and changing the code
 * to "get around it" leaves an orphan branch nobody knows about. The caller
 * should offer a resync instead of a resubmit.
 */
export class BranchPolicySyncError extends Error {
  readonly branchSaved = true;
  constructor(message: string) {
    super(message);
    this.name = "BranchPolicySyncError";
  }
}

function isPolicySyncFailure(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "error" in error &&
    typeof (error as { error?: { code?: unknown } }).error?.code === "string" &&
    (error as { error: { code: string } }).error.code === "branch_policy_sync_failed"
  );
}

export function useCreateBranch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: BranchUnitInput) => {
      const { data, error } = await api.POST("/branches", { body: input });
      if (isPolicySyncFailure(error)) {
        // Refresh first: the branch is in the catalog even though this throws.
        await qc.invalidateQueries({ queryKey: ["branches"] });
        throw new BranchPolicySyncError(
          "Đã tạo chi nhánh nhưng chưa đồng bộ Casdoor. Bấm “Đồng bộ Casdoor”, đừng tạo lại.",
        );
      }
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tạo được chi nhánh"));
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["branches"] }),
  });
}

export function useUpdateBranch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ code, input }: { code: string; input: BranchUnitUpdate }) => {
      const { data, error } = await api.PUT("/branches/{code}", {
        params: { path: { code } },
        body: input,
      });
      if (isPolicySyncFailure(error)) {
        await qc.invalidateQueries({ queryKey: ["branches"] });
        throw new BranchPolicySyncError(
          "Đã lưu chi nhánh nhưng chưa đồng bộ Casdoor. Bấm “Đồng bộ Casdoor”.",
        );
      }
      if (error || !data) throw new Error(serverErrorMessage(error, "Không cập nhật được chi nhánh"));
      // Advisory notes ride along with a successful write (for example,
      // capacity lowered below bookings already taken).
      return data as typeof data & { warnings?: string[] };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["branches"] }),
  });
}

export type BranchPendingRevocation = components["schemas"]["BranchPendingRevocation"];

/** Deactivated branches whose staff still hold access until the grace period ends. */
export function usePendingBranchRevocations() {
  return useQuery({
    queryKey: ["branches", "pending-revocations"],
    queryFn: async () => {
      const { data, error } = await api.GET("/branches/pending-revocations", {});
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được danh sách chờ thu hồi quyền"));
      return data;
    },
    staleTime: 60_000,
  });
}

export function useResyncBranchPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await api.POST("/branches/resync", {});
      if (error || !data) throw new Error(serverErrorMessage(error, "Không đồng bộ được quyền chi nhánh"));
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["branches"] }),
  });
}

export function useDeactivateBranch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (code: string) => {
      const { data, error } = await api.POST("/branches/{code}/deactivate", {
        params: { path: { code } },
      });
      // The branch is already deactivated when this comes back; only the
      // Casdoor scope policy lags. Same handling as create/update.
      if (isPolicySyncFailure(error)) {
        await qc.invalidateQueries({ queryKey: ["branches"] });
        throw new BranchPolicySyncError(
          "Đã ngưng hoạt động chi nhánh nhưng chưa đồng bộ Casdoor. Bấm “Đồng bộ Casdoor”.",
        );
      }
      if (error || !data) throw new Error(serverErrorMessage(error, "Không ngừng hoạt động được chi nhánh"));
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["branches"] }),
  });
}

// Chỉ đọc (QĐ 0516): phạm vi chi nhánh từng người do HRM ghi lên Casdoor.
export function useStaffBranchScope(staffId: string | null) {
  return useQuery({
    queryKey: ["staff", staffId, "branch-scopes"],
    enabled: !!staffId,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/staff/{id}/branch-scopes", {
        params: { path: { id: staffId! } },
      });
      if (error || !data) {
        throw Object.assign(new Error("Không thể tải phạm vi từ Casdoor"), { status: response.status });
      }
      return data;
    },
  });
}

// ══════════ PHÒNG PHỤ TRÁCH CỦA QC (migration 0331) ══════════

/**
 * Các phòng giám đốc gán tay cho một người QC.
 *
 * 🔴 `departments` RỖNG nghĩa là CHƯA GÁN GÌ — phạm vi đang chạy theo ô phòng
 * trong hồ sơ (`fallback`). Đừng vẽ "không phụ trách phòng nào" lên một người
 * đang phụ trách một phòng.
 */
export function useStaffQcDepartments(staffId: string | null) {
  return useQuery({
    queryKey: ["staff", staffId, "qc-departments"],
    enabled: !!staffId,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/staff/{id}/qc-departments", {
        params: { path: { id: staffId! } },
      });
      if (error || !data) {
        throw Object.assign(new Error("Không tải được phòng phụ trách"), {
          status: response.status,
        });
      }
      return data;
    },
  });
}

export function useUpdateStaffQcDepartments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ staffId, departments }: { staffId: string; departments: string[] }) => {
      const { data, error, response } = await api.PUT("/staff/{id}/qc-departments", {
        params: { path: { id: staffId } },
        body: { departments },
      });
      if (error || !data) {
        throw Object.assign(new Error("Không lưu được phòng phụ trách"), {
          status: response.status,
        });
      }
      return data;
    },
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: ["staff", variables.staffId, "qc-departments"] });
    },
  });
}

// ══════════ DANH PHẬN KIÊM NHIỆM (QĐ 0255) ══════════

export type StaffPersonasView = components["schemas"]["StaffPersonasView"];
export type PersonaRole = components["schemas"]["GrantStaffPersonaInput"]["role"];

/** Danh phận chính + các danh phận phụ còn hiệu lực của một người. */
export function useStaffPersonas(staffId: string | null) {
  return useQuery({
    queryKey: ["staff", staffId, "personas"],
    enabled: !!staffId,
    queryFn: async () => {
      const { data, error } = await api.GET("/staff/{id}/personas", {
        params: { path: { id: staffId! } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được danh phận"));
      return data;
    },
  });
}

export function useGrantStaffPersona() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      staffId,
      role,
      department,
    }: {
      staffId: string;
      role: PersonaRole;
      department: string;
    }) => {
      const { data, error } = await api.POST("/staff/{id}/personas", {
        params: { path: { id: staffId } },
        body: { role, department },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không gán được danh phận"));
      return data;
    },
    onSuccess: (data, v) => {
      qc.setQueryData(["staff", v.staffId, "personas"], data);
    },
  });
}

export function useRevokeStaffPersona() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ staffId, personaId }: { staffId: string; personaId: string }) => {
      const { data, error } = await api.DELETE("/staff/{id}/personas/{personaId}", {
        params: { path: { id: staffId, personaId } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không gỡ được danh phận"));
      return data;
    },
    onSuccess: (data, v) => {
      qc.setQueryData(["staff", v.staffId, "personas"], data);
    },
  });
}

// ══════════ AUDIT LOG ══════════

export function useStaffAudit(staffId: string | null, limit = 50) {
  return useQuery({
    queryKey: ["audit", "staff", staffId ?? "", limit],
    enabled: !!staffId,
    queryFn: async () => {
      const { data, error } = await api.GET("/audit", {
        params: {
          query: { entityType: "staff", entityId: staffId!, limit },
        },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được audit log"));
      return data;
    },
    staleTime: 30_000,
  });
}
